import Handlebars from 'handlebars';
import { ESalesChannel } from 'enums';
import {
  describeLineDiscountEquivalent,
  EDiscountEntryType,
  formatDiscountEntry,
  formatInvoiceDiscountEntry,
} from './discount-entry';
import { posReceiptTemplate } from './invoice';
import { calculateStoredSaleVatAmount, storedVatFractionToPercent } from './sale-vat';
import { formatCurrency } from './currency';

// The cash memo is the POS CASH RECEIPT (posReceiptTemplate). Stitches S7, behind
// `pos.cashMemoPreview`: a sample preview, and a POS sale reprinted from the sales table uses the
// receipt instead of the full invoice. Every other sale keeps the full invoice.

export type SaleReprintTemplate = 'cashMemo' | 'invoice';

export const chooseSaleReprintTemplate = (
  salesChannel: string | null | undefined,
  cashMemoEnabled: boolean,
): SaleReprintTemplate =>
  cashMemoEnabled && salesChannel === ESalesChannel.POS ? 'cashMemo' : 'invoice';

export type CashMemoSaleLine = {
  quantity: number;
  unitPrice: number;
  subTotal: number;
  discountCost?: number | null;
  discountEntryValue?: number | null;
  discountEntryType?: EDiscountEntryType | null;
  productVariant: { name: string; sku: string };
};

// A saved sale as the API returns it. `subTotal` is after line discounts and `vatAmount` is the
// stored VAT fraction (0.15 for 15%).
export type CashMemoSale = {
  id: string;
  salesDate: string | Date;
  subTotal: number;
  vatAmount?: number | null;
  discountCost?: number | null;
  discountEntryValue?: number | null;
  discountEntryType?: EDiscountEntryType | null;
  shipmentCost?: number | null;
  totalAmount: number;
  salesProductsCopy: CashMemoSaleLine[];
  paidAmount: number;
  processedBy?: string | null;
};

export type CashMemoCompany = {
  name?: string | null;
  number?: string | null;
  address?: string | null;
  tax?: string | null;
  bin?: string | null;
};

// The values posReceiptTemplate prints, from a saved sale. Cash is what was paid; the cash
// handed over at the till is not stored, so a reprint shows no change beyond any overpayment.
export const buildCashMemoReceiptData = (sale: CashMemoSale, company: CashMemoCompany) => {
  const lineDiscountTotal = sale.salesProductsCopy.reduce(
    (total, line) => total + (line.discountCost || 0),
    0,
  );
  const vatAmount = calculateStoredSaleVatAmount(sale.subTotal, sale.vatAmount);
  const salesDate = new Date(sale.salesDate);
  return {
    products: sale.salesProductsCopy.map((line) => ({
      sku: line.productVariant.sku,
      name: line.productVariant.name,
      quantity: line.quantity,
      unitPrice: formatCurrency(line.unitPrice),
      itemDiscount: formatDiscountEntry(
        line.discountEntryValue,
        line.discountEntryType,
        line.discountCost,
      ),
      itemDiscountEquivalent: describeLineDiscountEquivalent(
        line.quantity,
        line.unitPrice,
        line.discountCost,
        line.discountEntryValue,
        line.discountEntryType,
      ),
      netTotal: formatCurrency(line.subTotal),
    })),
    company,
    vat:
      vatAmount > 0
        ? {
            actualPrice: formatCurrency(sale.subTotal),
            percent: storedVatFractionToPercent(sale.vatAmount),
            amount: formatCurrency(vatAmount),
          }
        : null,
    netTotal: formatCurrency(sale.subTotal + vatAmount),
    total: formatCurrency(sale.totalAmount),
    perItemDiscount: formatCurrency(lineDiscountTotal),
    orderDiscountEntry: formatInvoiceDiscountEntry(
      sale.discountEntryValue,
      sale.discountEntryType,
      sale.discountCost,
    ),
    totalDiscount: formatCurrency(lineDiscountTotal + (sale.discountCost || 0)),
    // Printed only when the sale had shipping, so the lines above add up to the total.
    shipping: sale.shipmentCost ? formatCurrency(sale.shipmentCost) : null,
    cash: formatCurrency(sale.paidAmount),
    change: formatCurrency(Math.max(0, sale.paidAmount - sale.totalAmount)),
    invoiceId: sale.id,
    processedBy: sale.processedBy ?? null,
    date: new Intl.DateTimeFormat('en-GB').format(salesDate),
    time: new Intl.DateTimeFormat('en-GB', { timeStyle: 'short' }).format(salesDate),
  };
};

// Same row partial as the POS receipt in pages/pos/list.tsx.
const cashMemoProductRow = `<tr>
  <td>{{ product.sku }}</td>
  <td>{{ product.name }}</td>
  <td>{{ product.quantity }}</td>
  <td>{{ product.unitPrice }}</td>
  <td>{{ product.itemDiscount }}{{#if product.itemDiscountEquivalent}}<br /><small style="color:#666">{{ product.itemDiscountEquivalent }}</small>{{/if}}</td>
  <td>{{ product.netTotal }}</td>
</tr>`;

export const renderCashMemo = (
  receiptData: ReturnType<typeof buildCashMemoReceiptData>,
): string => {
  Handlebars.registerHelper('incremented', (index: number) => index + 1);
  Handlebars.registerPartial('productTableRow', cashMemoProductRow);
  return Handlebars.compile(posReceiptTemplate)(receiptData);
};

// Two lines, a line discount, an order discount, VAT and change: every part of the memo shows.
// Nothing is saved; the sample only feeds the template.
export const buildSampleCashMemoSale = (vatRatePercent: number): CashMemoSale => {
  const lines: CashMemoSaleLine[] = [
    {
      quantity: 2,
      unitPrice: 1200,
      subTotal: 2160,
      discountCost: 240,
      discountEntryValue: 10,
      discountEntryType: EDiscountEntryType.Percent,
      productVariant: { name: 'Cotton Panjabi/White/M', sku: 'SAMPLE-PNJ-WM' },
    },
    {
      quantity: 1,
      unitPrice: 850,
      subTotal: 850,
      productVariant: { name: 'Printed Kurti/Blue/S', sku: 'SAMPLE-KRT-BS' },
    },
  ];
  const subTotal = lines.reduce((total, line) => total + line.subTotal, 0);
  const storedVatFraction = Number((vatRatePercent / 100).toFixed(4));
  const orderDiscount = 10;
  const totalAmount = Number(
    (subTotal + calculateStoredSaleVatAmount(subTotal, storedVatFraction) - orderDiscount).toFixed(
      2,
    ),
  );
  return {
    id: 'SAMPLE',
    salesDate: new Date(),
    subTotal,
    vatAmount: storedVatFraction,
    discountCost: orderDiscount,
    discountEntryValue: orderDiscount,
    discountEntryType: EDiscountEntryType.Amount,
    totalAmount,
    salesProductsCopy: lines,
    paidAmount: Math.ceil(totalAmount / 500) * 500,
    processedBy: 'Sample',
  };
};
